import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Container,
  Card,
  CardContent,
  Alert,
  styled
} from '@mui/material';
import { Link } from 'react-router-dom';
import { useRequestPasswordReset } from '../../../../hooks/usePasswordReset';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const ContentWrapper = styled(Box)(
  () => `
    height: 100%;
    display: flex;
    flex: 1;
    overflow: auto;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`
);

export function ForgotPasswordPage(): JSX.Element {
  const { palette: _palette } = useTheme();
  const _navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  const forgotPasswordMutation = useRequestPasswordReset();

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    if (!email.trim()) {
      setEmailError(t('forgotPassword.emailRequired', 'Email is required'));
      return;
    } else {
      setEmailError(null);
    }

    try {
      await forgotPasswordMutation.mutateAsync(email);
      setEmailSent(true);
    } catch (error) {
      console.error('Error requesting password reset:', error);
      // Even if there's an error, we show success message for security reasons
      setEmailSent(true);
    }
  };

  return (
    <ContentWrapper>
      <Container maxWidth="sm">
        <Card>
          <CardContent sx={{ p: 4 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3
              }}
            >
              <Typography variant="h3" component="h1">
                {t('forgotPassword.title', 'Reset Password')}
              </Typography>

              {!emailSent ? (
                <>
                  <Typography
                    variant="body1"
                    sx={{ textAlign: 'center', mb: 2 }}
                  >
                    {t(
                      'forgotPassword.instructions',
                      "Enter your email address and we'll send you a link to reset your password."
                    )}
                  </Typography>

                  <Box
                    component="form"
                    onSubmit={handleSubmit}
                    noValidate
                    sx={{ width: '100%' }}
                  >
                    <TextField
                      fullWidth
                      label={t('forgotPassword.email', 'Email Address')}
                      type="email"
                      variant="outlined"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      error={!!emailError}
                      helperText={emailError}
                      margin="normal"
                      autoFocus
                    />

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      color="primary"
                      size="large"
                      sx={{ mt: 3 }}
                      disabled={forgotPasswordMutation.isPending}
                    >
                      {forgotPasswordMutation.isPending
                        ? t('forgotPassword.sending', 'Sending...')
                        : t('forgotPassword.sendLink', 'Send Reset Link')}
                    </Button>
                  </Box>
                </>
              ) : (
                <>
                  <Alert severity="success" sx={{ width: '100%' }}>
                    {t(
                      'forgotPassword.success',
                      "If an account exists with that email, we've sent a password reset link."
                    )}
                  </Alert>
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    {t(
                      'forgotPassword.checkEmail',
                      'Please check your email and follow the instructions to reset your password.'
                    )}
                  </Typography>
                </>
              )}

              <Box sx={{ mt: 2 }}>
                <Link to="/login">
                  <Button variant="text">
                    {t('forgotPassword.backToLogin', 'Back to Login')}
                  </Button>
                </Link>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </ContentWrapper>
  );
}

export default ForgotPasswordPage;
